import { useCallback, useEffect, useMemo, useState } from 'react';
import useAuthSession from './useAuthSession';
import { obtenerToken } from '../services/authService';
import { obtenerMisPermisosEmpresa } from '../services/empresasPermisosService';
import {
  esAdminCn as esAdminCnHelper,
  obtenerRolEmpresaPrincipal,
  tieneAccesoEmpresarial as tieneAccesoEmpresarialHelper,
  puedeCrearProyecto as puedeCrearProyectoHelper,
  puedeEditarProyecto as puedeEditarProyectoHelper,
  puedeEditarContenidoProyecto as puedeEditarContenidoProyectoHelper,
  puedeEditarUnidades as puedeEditarUnidadesHelper,
  puedeEditarModelos as puedeEditarModelosHelper,
  puedeSubirPlano as puedeSubirPlanoHelper,
  puedeSubirImagenes as puedeSubirImagenesHelper,
  puedeImportarCsv as puedeImportarCsvHelper,
  puedeOperarProspectos as puedeOperarProspectosHelper,
  puedeOperarApartados as puedeOperarApartadosHelper,
  soloLectura as soloLecturaHelper,
} from '../utils/permisosProyecto';

const ROLES_INTERNOS_CN = ['ASESOR', 'SUPERVISOR', 'ADMIN', 'SUPERADMIN'];
const NO_PERMISSION_EMPRESA_MESSAGE = 'No tienes permiso para administrar esta empresa.';

const empresaKey = (value) => String(value || '').trim();

const crearSesionConPermisosEmpresa = (sesion = {}, permisosEmpresa = null) => {
  const empresasPermisos = Array.isArray(permisosEmpresa?.empresas) ? permisosEmpresa.empresas : [];
  const empresasSesion = Array.isArray(sesion.empresas) ? sesion.empresas : [];
  const permisosCargados = permisosEmpresa !== null;
  const empresas = permisosCargados ? empresasPermisos : empresasSesion;

  return {
    ...sesion,
    empresas,
    tieneAccesoEmpresarial: permisosCargados
      ? empresasPermisos.length > 0
      : Boolean(sesion.tieneAccesoEmpresarial || empresasSesion.length > 0),
    puedeAdministrarEmpresa: Boolean(
      (permisosCargados ? false : sesion.puedeAdministrarEmpresa) ||
      empresasPermisos.length > 0 ||
      (!permisosCargados && empresasSesion.some((empresa) => empresa?.activo !== false && String(empresa?.rolEmpresa || '').toUpperCase() === 'ADMIN_EMPRESA'))
    ),
  };
};

export default function usePermisosEmpresa() {
  const sesion = useAuthSession();
  const [permisosEmpresa, setPermisosEmpresa] = useState(null);
  const [cargandoPermisosEmpresa, setCargandoPermisosEmpresa] = useState(false);
  const [errorPermisosEmpresa, setErrorPermisosEmpresa] = useState('');
  const token = obtenerToken();
  const rolSesion = String(sesion.rolGlobal || sesion.usuario?.rolGlobal || sesion.usuario?.rol || '').toUpperCase();
  const esRolInternoSesion = ROLES_INTERNOS_CN.includes(rolSesion);

  const recargarPermisosEmpresa = useCallback(async (options = {}) => {
    if (!token) {
      setPermisosEmpresa(null);
      setErrorPermisosEmpresa('');
      setCargandoPermisosEmpresa(false);
      return null;
    }

    if (esRolInternoSesion) {
      setPermisosEmpresa(null);
      setErrorPermisosEmpresa('');
      setCargandoPermisosEmpresa(false);
      return null;
    }

    setCargandoPermisosEmpresa(true);
    setErrorPermisosEmpresa('');

    try {
      const data = await obtenerMisPermisosEmpresa({
        forceRefresh: options.forceRefresh ?? false,
        cacheKey: `${sesion.usuarioId || ''}:${token}`,
        signal: options.signal,
      });
      setPermisosEmpresa(data);
      return data;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setPermisosEmpresa(null);
        setErrorPermisosEmpresa(err.data?.mensaje || err.data?.message || err.message || 'No fue posible cargar tus permisos de empresa.');
      }
      return null;
    } finally {
      if (!options.signal?.aborted) {
        setCargandoPermisosEmpresa(false);
      }
    }
  }, [esRolInternoSesion, sesion.usuarioId, token]);

  useEffect(() => {
    if (sesion.cargando) {
      return undefined;
    }

    const controller = new AbortController();
    recargarPermisosEmpresa({ signal: controller.signal, forceRefresh: false });

    return () => {
      controller.abort();
    };
  }, [recargarPermisosEmpresa, rolSesion, sesion.cargando, sesion.usuarioId]);

  const sesionConPermisosEmpresa = useMemo(
    () => crearSesionConPermisosEmpresa(sesion, permisosEmpresa),
    [permisosEmpresa, sesion]
  );
  const rolGlobal = String(sesionConPermisosEmpresa.rolGlobal || sesionConPermisosEmpresa.usuario?.rolGlobal || sesionConPermisosEmpresa.usuario?.rol || '').toUpperCase();
  const esInternoCn = ROLES_INTERNOS_CN.includes(rolGlobal);
  const esAdminCn = esAdminCnHelper(sesionConPermisosEmpresa);
  const tieneEmpresa = tieneAccesoEmpresarialHelper(sesionConPermisosEmpresa);
  const rolEmpresa = useMemo(() => obtenerRolEmpresaPrincipal(sesionConPermisosEmpresa), [sesionConPermisosEmpresa]);
  const empresas = useMemo(
    () => (Array.isArray(sesionConPermisosEmpresa.empresas) ? sesionConPermisosEmpresa.empresas : []),
    [sesionConPermisosEmpresa.empresas]
  );
  const empresasAdministrables = useMemo(
    () => empresas.filter((empresa) =>
      empresa?.activo !== false &&
      String(empresa?.rolEmpresa || '').toUpperCase() === 'ADMIN_EMPRESA' &&
      (empresa.puedeAdministrar !== false)
    ),
    [empresas]
  );
  const empresasAdministrablesIds = useMemo(
    () => new Set(empresasAdministrables.map((empresa) => empresaKey(empresa.id || empresa.empresaId))),
    [empresasAdministrables]
  );
  const tieneEmpresaAdministrable = empresasAdministrables.length > 0;
  const puedeAdministrarEmpresaId = useCallback((empresaId) =>
    esAdminCn || empresasAdministrablesIds.has(empresaKey(empresaId)), [empresasAdministrablesIds, esAdminCn]);
  const obtenerErrorEmpresaNoPermitida = useCallback(
    (empresaId) => (empresaId && !puedeAdministrarEmpresaId(empresaId) ? NO_PERMISSION_EMPRESA_MESSAGE : ''),
    [puedeAdministrarEmpresaId]
  );
  const permisosEmpresaPendientes = Boolean(
    token &&
    !sesion.cargando &&
    !esRolInternoSesion &&
    permisosEmpresa === null &&
    !errorPermisosEmpresa
  );

  return {
    cargando: sesion.cargando || cargandoPermisosEmpresa || permisosEmpresaPendientes,
    cargandoSesion: sesion.cargando,
    cargandoPermisosEmpresa,
    errorPermisosEmpresa,
    recargarPermisosEmpresa,
    usuario: sesionConPermisosEmpresa.usuario,
    rolGlobal,
    rolEmpresa,
    empresas,
    empresasAdministrables,
    empresasAdministrablesIds,
    esInternoCn,
    esAdminCn,
    tieneEmpresa,
    tieneEmpresaAdministrable,
    puedeAdministrarEmpresaId,
    obtenerErrorEmpresaNoPermitida,
    puedePublicarPropiedades: Boolean(sesionConPermisosEmpresa.puedePublicarPropiedades),
    puedeCrearProyectos: Boolean(sesionConPermisosEmpresa.puedeCrearProyectos),
    puedeAdministrarEmpresa: Boolean(sesionConPermisosEmpresa.puedeAdministrarEmpresa),
    puedeAccederProyectos: esAdminCn || tieneEmpresaAdministrable || Boolean(sesionConPermisosEmpresa.puedeCrearProyectos),
    puedeCrearProyecto: puedeCrearProyectoHelper(sesionConPermisosEmpresa),
    puedeEditarProyecto: puedeEditarProyectoHelper(sesionConPermisosEmpresa),
    puedeEditarContenidoProyecto: puedeEditarContenidoProyectoHelper(sesionConPermisosEmpresa),
    puedeEditarUnidades: puedeEditarUnidadesHelper(sesionConPermisosEmpresa),
    puedeEditarModelos: puedeEditarModelosHelper(sesionConPermisosEmpresa),
    puedeSubirPlano: puedeSubirPlanoHelper(sesionConPermisosEmpresa),
    puedeSubirImagenes: puedeSubirImagenesHelper(sesionConPermisosEmpresa),
    puedeImportarCsv: puedeImportarCsvHelper(sesionConPermisosEmpresa),
    puedeOperarProspectos: puedeOperarProspectosHelper(sesionConPermisosEmpresa),
    puedeOperarApartados: puedeOperarApartadosHelper(sesionConPermisosEmpresa),
    puedeOperarComercial: puedeOperarProspectosHelper(sesionConPermisosEmpresa) || puedeOperarApartadosHelper(sesionConPermisosEmpresa),
    soloLectura: soloLecturaHelper(sesionConPermisosEmpresa),
  };
}
